import { useEffect, useRef, useState } from 'react'

type SaveResponse = { status: number }
type SaveOptions<Result extends SaveResponse> = {
    isNew: boolean
    getSavedID: (response: Result) => unknown
}
type SaveSession = { active: boolean; pending: boolean; blocked: boolean }

export const useCardSaveGuard = (isOpen: boolean, cardKey: string, onUncertain: () => void) => {
    const [isBlocked, setIsBlocked] = useState(false)
    const sessionRef = useRef<SaveSession>({ active: isOpen, pending: false, blocked: false })

    useEffect(() => {
        const session = { active: isOpen, pending: false, blocked: false }
        sessionRef.current = session
        setIsBlocked(false)
        return () => { session.active = false }
    }, [isOpen, cardKey])

    const runSave = async <Result extends SaveResponse>(
        save: () => Promise<Result>,
        options: SaveOptions<Result>,
    ): Promise<Result | undefined> => {
        const session = sessionRef.current
        if (!session.active || session.pending || session.blocked) return
        session.pending = true

        const markUncertain = () => {
            session.blocked = true
            setIsBlocked(true)
            onUncertain()
        }

        try {
            const response = await save()
            if (!session.active || sessionRef.current !== session) return

            if (options.isNew) {
                const savedID = response.status === 200 ? Number(options.getSavedID(response)) : 0
                const missingID = response.status === 200 && (!Number.isSafeInteger(savedID) || savedID <= 0)
                const uncertainStatus = !Number.isInteger(response.status) || response.status >= 500 || [0, 408, 499].includes(response.status)
                if (missingID || uncertainStatus) {
                    markUncertain()
                    return
                }
            }

            return response
        } catch (error) {
            if (!session.active || sessionRef.current !== session) return
            if (options.isNew) {
                markUncertain()
                return
            }
            throw error
        } finally {
            session.pending = false
        }
    }

    return { runSave, isBlocked }
}